import React from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
const uuid = require('uuid');
const Moment = require('moment');
import DatePicker from 'react-datepicker';
import { Row, Button } from 'react-bootstrap';
const get = require('lodash/get');
const isArray = require('lodash/isArray');

const Globals = require('../../../../app/globals');
const Strings = require('../../../../app/shared/Strings');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const StringUtil = require('../../../../app/shared/StringUtil');
const ChartUtil = require('../../../../app/shared/ChartUtil');
const AnalyticsStore = require('../../stores/AnalyticsStore');
const AnalyticsActions = require('../../actions/AnalyticsActions');
const ServerRequest = require('../../actions/request');
import LoadingMainContent from '../loading/LoadingMainContent';
import LoadingWithCancel from '../loading/LoadingWithCancel';
import TimeseriesChart from '../chart/TimeseriesChart';
import AccessibleIcon from '../common/AccessibleIcon';
import StatTile from '../tiles/StatTile';
import { ChartTotalsBoxRow, ChartTotalsBox, ChartTotalsBoxItem } from '../chart/ChartTotalsBox';
import OrgNewStudentsChart from '../organization/OrgNewStudentsChart';
import SortableTable2 from '../table/SortableTable2';
import AssignmentAnalyticsOverview from './AssignmentAnalyticsOverview';
import ChannelShareRangeChart from './ChannelShareRangeChart';

const propTypes = {
    channelId: PropTypes.number.isRequired,
    channelCreateDate: PropTypes.string.isRequired,
    userPermissions: PropTypes.object,
    channelPermissions: PropTypes.object,
    backHandler: PropTypes.func,
    heading: PropTypes.string
};

const defaultProps = {
    userPermissions: {},
    channelPermissions: {},
    backHandler: null,
    heading: ""
};

class ChannelAnalyticsView extends React.Component {
    constructor(props) {
        super(props);

        // determine start/end date for existing store data
        let startDate;
        let endDate;
        let rangeDirty;
        const rangeTimeseries = get(AnalyticsStore.getState().overviewAnalyticsRange, [props.channelId, 'timeseriesCol'], null);
        if (isArray(rangeTimeseries)) {
            startDate = Moment(rangeTimeseries[1]).toDate(); // first value in server-generated c3js column
            endDate = Moment(rangeTimeseries[rangeTimeseries.length - 1]).toDate();
            rangeDirty = false;
        }
        else {
            startDate = Moment().subtract(1, 'M').toDate();
            endDate = new Date();
            rangeDirty = true;
        }
        this.pollTaskManager = new ServerRequest.PollTaskManager();

        this.state = {
            analyticsStoreState: AnalyticsStore.getState(),
            startDate: startDate,
            endDate: endDate,
            selectedDate: null,
            rangeDirty: rangeDirty,
            actionID: null,
            overviewAnalyticsRangeActionId: null,
            getNewStudAnalyticsActionId: null,
            getPublishedActCountsActionId: null,
            forceShowNewStudents: false
        };

        this.shouldGetChannelOverview = this.shouldGetChannelOverview.bind(this);
        // this.shouldGetChannelOverviewRange = this.shouldGetChannelOverviewRange.bind(this);
        // this.shouldGetNewStudentStats = this.shouldGetNewStudentStats.bind(this);
        this.shouldGetPublishedActCounts = this.shouldGetPublishedActCounts.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
        this.onDateSelect = this.onDateSelect.bind(this);
        this.onOverviewRangeUpdate = this.onOverviewRangeUpdate.bind(this);
        this.cancelOverviewRange = this.cancelOverviewRange.bind(this);
        this.toggleForceShowNewStudents = this.toggleForceShowNewStudents.bind(this);

        this.unsubscribes = [AnalyticsStore.listen(this.onStoreChange.bind(this, "analyticsStoreState"))];
    }

    componentDidMount() {
        const shouldGetChannelOverview = this.shouldGetChannelOverview();
        const shouldGetChannelOverviewRange = false; // do not fetch on mount for performance reasons Redmine #5727
        const shouldGetNewStudentStats = false;
        const shouldGetPublishedActCounts = this.shouldGetPublishedActCounts();
        if (
            !shouldGetChannelOverview
            && !shouldGetChannelOverviewRange
            && !shouldGetNewStudentStats
        ) {
            return;
        }

        let newState = {};
        if (shouldGetChannelOverview) {
            newState.actionID = uuid.v4();
        }

        if (shouldGetChannelOverviewRange) {
            newState.overviewAnalyticsRangeActionId = uuid.v4();
        }

        if (shouldGetNewStudentStats) {
            newState.getNewStudAnalyticsActionId = uuid.v4();
        }

        if (shouldGetPublishedActCounts) {
            newState.getPublishedActCountsActionId = uuid.v4();
        }

        this.setState(newState, () => {
            if (shouldGetChannelOverview) {
                AnalyticsActions.getChannelOverview(
                    newState.actionID,
                    { id: this.props.channelId }
                );
            }

            if (shouldGetChannelOverviewRange) {
                AnalyticsActions.getChannelOverviewRange(
                    newState.overviewAnalyticsRangeActionId,
                    { id: this.props.channelId, startDate: Moment(this.state.startDate), endDate: Moment(this.state.endDate) },
                    this.pollTaskManager
                );
            }
            if (shouldGetNewStudentStats) {
                AnalyticsActions.getNewStudentStatsRange(
                    newState.getNewStudAnalyticsActionId,
                    { orgId: this.props.channelId, startDate: Moment(this.state.startDate), endDate: Moment(this.state.endDate) },
                    this.pollTaskManager
                );
            }
            if (shouldGetPublishedActCounts) {
                AnalyticsActions.getChannelPublishedActCounts(
                    newState.getPublishedActCountsActionId,
                    { id: this.props.channelId }
                );
            }
        });
    }

    componentWillUnmount() {
        this.unsubscribes.forEach(u => u());
        this.pollTaskManager.abandonTasks();
    }

    onStoreChange(storeStateName, state) {
        let additionalState = {};
        if (state.action.id === this.state.actionID) {
            additionalState.actionID = null;
        }
        if (state.action.id === this.state.overviewAnalyticsRangeActionId) {
            additionalState.overviewAnalyticsRangeActionId = null;
            additionalState.rangeDirty = false;
        }
        if (state.action.id === this.state.getNewStudAnalyticsActionId) {
            additionalState.getNewStudAnalyticsActionId = null;
            additionalState.rangeDirty = false;
        }
        if (state.action.id === this.state.getPublishedActCountsActionId) {
            additionalState.getPublishedActCountsActionId = null;
        }

        this.setState({ [storeStateName]: state, ...additionalState });
    }

    shouldGetChannelOverview() {
        return !this.state.analyticsStoreState.overviewAnalytics[this.props.channelId];
    }

    /*
    shouldGetChannelOverviewRange() {
        const analyticsStoreState = this.state.analyticsStoreState;
        if (!analyticsStoreState.overviewAnalyticsRange[this.props.channelId]) {
            return true;
        }

        if (
            !analyticsStoreState.overviewAnalyticsRangeDates[this.props.channelId]
            || analyticsStoreState.overviewAnalyticsRangeDates[this.props.channelId].startDate !== Moment(this.state.startDate).format('YYYY-MM-DD')
            || analyticsStoreState.overviewAnalyticsRangeDates[this.props.channelId].endDate !== Moment(this.state.endDate).format('YYYY-MM-DD')
        ) {
            return true;
        }

        return false;
    }

    shouldGetNewStudentStats() {
        const analyticsStoreState = this.state.analyticsStoreState;
        if (!analyticsStoreState.newStudentStats[this.props.channelId]) {
            return true;
        }

        if (
            !analyticsStoreState.newStudentStatsDates[this.props.channelId]
            || analyticsStoreState.newStudentStatsDates[this.props.channelId].startDate !== Moment(this.state.startDate).format('YYYY-MM-DD')
            || analyticsStoreState.newStudentStatsDates[this.props.channelId].endDate !== Moment(this.state.endDate).format('YYYY-MM-DD')
        ) {
            return true;
        }

        return false;
    }
    */

    shouldGetPublishedActCounts() {
        return !this.state.analyticsStoreState.publishedActChannelCounts[this.props.channelId];
    }

    onStartDateChange(date) {
        this.setState({ rangeDirty: true, startDate: date });
    }

    onEndDateChange(date) {
        this.setState({ rangeDirty: true, endDate: date });
    }

    onDateSelect(newDate) {
        this.setState({ selectedDate: newDate });
    }

    toggleForceShowNewStudents(e) {
        this.setState({ forceShowNewStudents: !this.state.forceShowNewStudents });
    }

    onOverviewRangeUpdate(e) {
        const overviewAnalyticsRangeActionId = uuid.v4();
        const getNewStudAnalyticsActionId = uuid.v4();
        if (this.state.overviewAnalyticsRangeActionId) {
            this.pollTaskManager.cancelAction(this.state.overviewAnalyticsRangeActionId);
        }
        if (this.state.getNewStudAnalyticsActionId) {
            this.pollTaskManager.cancelAction(this.state.getNewStudAnalyticsActionId);
        }
        this.setState(
            {
                overviewAnalyticsRangeActionId: overviewAnalyticsRangeActionId,
                getNewStudAnalyticsActionId: getNewStudAnalyticsActionId,
            },
            () => {           
                AnalyticsActions.getChannelOverviewRange(
                    overviewAnalyticsRangeActionId,
                    { id: this.props.channelId, startDate: Moment(this.state.startDate), endDate: Moment(this.state.endDate) },
                    this.pollTaskManager
                );

                AnalyticsActions.getNewStudentStatsRange(
                    getNewStudAnalyticsActionId,
                    { orgId: this.props.channelId, startDate: Moment(this.state.startDate), endDate: Moment(this.state.endDate) },
                    this.pollTaskManager
                );
            }
        );
    }

    cancelOverviewRange() {
        if (this.state.overviewAnalyticsRangeActionId) {
            this.pollTaskManager.cancelAction(this.state.overviewAnalyticsRangeActionId);
        }
        if (this.state.getNewStudAnalyticsActionId) {
            this.pollTaskManager.cancelAction(this.state.getNewStudAnalyticsActionId);
        }
        this.setState({
            overviewAnalyticsRangeActionId: null,
            getNewStudAnalyticsActionId: null,
            // rangeDirty: false,
        });
    }

    render() {
        const _overviewAnalytics = this.state.analyticsStoreState.overviewAnalytics[this.props.channelId] ? 
            this.state.analyticsStoreState.overviewAnalytics[this.props.channelId].analytics
            :
            null;
        const _overviewAnalyticsRange = this.state.analyticsStoreState.overviewAnalyticsRange[this.props.channelId];

        let isOrg = false;
        if (!!_overviewAnalytics) {
            isOrg = [Globals.users.account_types.SCHOOL, Globals.users.account_types.ENTERPRISE].indexOf(_overviewAnalytics.accountType) >= 0;
        }

        const xsClass = 'col-12';
        const smClass = isOrg ? 'col-sm-12' : 'col-sm-4';
        const mdClass = isOrg ? 'col-md-3' : 'col-md-4';

        let studentsForDate = [];
        if (this.state.selectedDate) {
            let newStudentStats = this.state.analyticsStoreState.newStudentStats[this.props.channelId];
            if (newStudentStats && newStudentStats.studentDetails) {
                studentsForDate = newStudentStats.studentDetails[this.state.selectedDate] || [];
            }
        }

        const _publishedActCounts = this.state.analyticsStoreState.publishedActChannelCounts[this.props.channelId];
        const additionalRangeButtonProps = {};

        if (!this.state.rangeDirty) {
            additionalRangeButtonProps.title = Strings['en'].analytics.overview.assignment_stats.UPDATE_RANGE_CLEAN_TT;
        }

        return (
            <div>
                <div className="channelHeadingFlex">
                    <div className="channelHeadingFlex__heading">
                        {(!!this.props.backHandler) &&
                            <button
                                className="btn btn-primary glyph btn-circle btn-lg margin-right-15"
                                onClick={this.props.backHandler}
                                title="Back"
                            >
                                <AccessibleIcon
                                label="Back">
                                    <span className="fa fa-chevron-left" aria-hidden={true}/>
                                </AccessibleIcon>
                            </button>
                        }
                        <h1 className="heading-lg">{this.props.heading}</h1>
                    </div>
                    <div className="channelHeadingFlex__rightControl">
                        <Link className="link-plain btn btn-primary" to={`/analytics/${this.props.channelId}/projects`}>
                            Project Analytics
                        </Link>
                    </div>
                </div>
                <div className="float-clear"/>

                <div className="tile-dash-floating">
                    {(!!_overviewAnalytics) ?
                        <div>
                            <h2 className="large-heading margin-bottom-lg">{Strings['en'].analytics.overview.channel_authoring_details.SECTION_HEADING}</h2>
                            <Row>
                                <StatTile
                                    className={`font-size-xl text-centered mzl-border-right ${xsClass} ${smClass} ${mdClass}`}
                                    term={Strings['en'].analytics.overview.channel_authoring_details.PROJECTS}
                                >
                                    {StringUtil.numberToLocaleString(_overviewAnalytics.projectCount)}
                                </StatTile>

                                <StatTile
                                    className={`font-size-xl text-centered mzl-border-right ${xsClass} ${smClass} ${mdClass}`}
                                    term={Strings['en'].analytics.overview.channel_authoring_details.ACTIVITIES}
                                >
                                    {StringUtil.numberToLocaleString(_overviewAnalytics.activityCount)}
                                </StatTile>

                                {(isOrg) &&
                                    <StatTile
                                        className={`font-size-xl text-centered mzl-border-right ${xsClass} ${smClass} ${mdClass}`}
                                        term={Strings['en'].analytics.overview.channel_authoring_details.MEMBERS}
                                    >
                                        {StringUtil.numberToLocaleString(_overviewAnalytics.memberCount)}
                                    </StatTile>
                                }

                                <StatTile
                                    className={`font-size-xl text-centered ${xsClass} ${smClass} ${mdClass}`}
                                    term={Strings['en'].analytics.overview.channel_authoring_details.COLLABORATORS}
                                >
                                    {StringUtil.numberToLocaleString(_overviewAnalytics.collabCount)}
                                </StatTile>
                            </Row>

                            <div className="margin-bottom-xl"/>

                            <h2 className="large-heading margin-bottom-lg">{Strings['en'].analytics.overview.channel_assn_analytics_overview.SECTION_HEADING}</h2>
                            <AssignmentAnalyticsOverview overviewAnalytics={_overviewAnalytics} />
                        </div>
                        :
                        <LoadingMainContent />
                    }

                    <h2 className="large-heading margin-bottom-lg">{Strings['en'].analytics.overview.share_stats.SECTION_HEADING}</h2>
                    <ChannelShareRangeChart channelId={this.props.channelId} />

                    <h2 className="large-heading margin-bottom-lg" onDoubleClick={this.toggleForceShowNewStudents}>{Strings['en'].analytics.overview.assignment_stats.SECTION_HEADING}</h2>
                    <div className="sub-heading">{Strings['en'].analytics.overview.assignment_stats.OPT_SUB_HEADING}</div>
                    <Row className="margin-bottom-lg">
                        <div className="col-12 col-sm-5 margin-bottom-sm">
                            <label className="label-form margin-right-sm">
                                {Strings['en'].common.START_DATE}
                            </label>
                            <DatePicker
                                className="margin-right-md"
                                selected={this.state.startDate}
                                onChange={this.onStartDateChange}
                                minDate={Moment(this.props.channelCreateDate).toDate()}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-12 col-sm-5 margin-bottom-sm">
                            <label className="label-form margin-right-sm">
                                {Strings['en'].common.END_DATE}
                            </label>
                            <DatePicker
                                className="margin-right-md"
                                selected={this.state.endDate}
                                onChange={this.onEndDateChange}
                                minDate={this.state.startDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-12 col-sm-2 margin-bottom-sm">
                            {(!!this.state.overviewAnalyticsRangeActionId || !!this.state.getNewStudAnalyticsActionId) ?
                            <div className="small-loading-with-cancel">
                                <LoadingWithCancel onCancel={this.cancelOverviewRange}/>
                            </div>
                            :
                            <Button
                                className="chart-update-button-fixed"
                                disabled={!this.state.rangeDirty}
                                onClick={this.onOverviewRangeUpdate}
                                {...additionalRangeButtonProps}
                            >
                                {Strings['en'].common.UPDATE}
                            </Button>
                            }
                        </div>
                    </Row>
                    {(!!_overviewAnalyticsRange && !! _overviewAnalyticsRange.assignmentsCol) ?
                        <div className="margin-bottom-xl">
                            <h3 className="md-heading margin-bottom-lg">{Strings['en'].analytics.overview.assignment_stats.general_stats.SECTION_HEADING}</h3>
                            <div className="sub-heading">{Strings['en'].analytics.overview.assignment_stats.general_stats.SUB_HEADING}</div>
                            <TimeseriesChart
                                id="stats-range-chart"
                                height={300}
                                labelShow={false}
                                legendShow
                                yInt
                                columns={[_overviewAnalyticsRange.timeseriesCol, _overviewAnalyticsRange.assignmentsCol, _overviewAnalyticsRange.attemptsCol, _overviewAnalyticsRange.activeStudentsCol]}
                            />
                            <ChartTotalsBoxRow>
                                <ChartTotalsBox title={Strings['en'].common.TOTALS} className="chart-totals-wide margin-bottom-lg">
                                    <ChartTotalsBoxItem
                                        label={_overviewAnalyticsRange.assignmentsCol[0]}
                                        value={ChartUtil.sumC3Column(_overviewAnalyticsRange.assignmentsCol)}
                                    />
                                    {(typeof _overviewAnalyticsRange.nonEmptyAssnCount === 'number') &&
                                        <ChartTotalsBoxItem label={Strings['en'].analytics.COUNT_NON_EMPTY_ASSN} value={_overviewAnalyticsRange.nonEmptyAssnCount} />
                                    }
                                    <ChartTotalsBoxItem
                                        label={_overviewAnalyticsRange.attemptsCol[0]}
                                        value={ChartUtil.sumC3Column(_overviewAnalyticsRange.attemptsCol)}
                                    />
                                    <ChartTotalsBoxItem
                                        label={_overviewAnalyticsRange.activeStudentsCol[0]}
                                        value={_overviewAnalyticsRange.activeStudentsTotal}
                                    />
                                </ChartTotalsBox>
                            </ChartTotalsBoxRow>

                            {(FeatureUtil.hasLTI(this.props.channelPermissions) || this.state.forceShowNewStudents) &&
                                <div>
                                    <h3 className="md-heading margin-bottom-lg">{Strings['en'].analytics.overview.assignment_stats.new_students.SECTION_HEADING}</h3>
                                    <div className="sub-heading">{Strings['en'].analytics.overview.assignment_stats.new_students.SUB_HEADING}</div>
                                    <OrgNewStudentsChart
                                        className="margin-bottom-lg"
                                        orgId={this.props.channelId}
                                        startDate={this.state.startDate}
                                        endDate={this.state.endDate}
                                        onDateSelect={this.onDateSelect}
                                    />
                                    <h3 className="md-heading margin-bottom-lg">{Strings['en'].analytics.overview.assignment_stats.student_details.SECTION_HEADING}</h3>
                                    <div className="sub-heading">{Strings['en'].analytics.overview.assignment_stats.student_details.SUB_HEADING}</div>
                                    {(!!this.state.selectedDate) ?
                                        <SortableTable2
                                            className="width-100p"
                                            classNameTableContainer="tableContainerXScroll tableMaxHeightMdScroll"
                                            hideSearch
                                            rows={studentsForDate}
                                            defaultSort={{ key: 'last_name', type: 'string', asc: true }}
                                            dateFormat="YYYY-MM-DD HH:mm:ss z"
                                            columns={[
                                                {
                                                    displayName: Strings['en'].analytics.overview.assignment_stats.student_details.LAST_NAME,
                                                    key:'last_name',
                                                    size: 2,
                                                    type: 'string'
                                                },
                                                {
                                                    displayName: Strings['en'].analytics.overview.assignment_stats.student_details.FIRST_NAME,
                                                    key:'first_name',
                                                    size: 2,
                                                    type: 'string'
                                                },
                                                {
                                                    displayName: Strings['en'].analytics.overview.assignment_stats.student_details.EMAIL,
                                                    key: 'third_party_email',
                                                    size: 3,
                                                    type: 'string'
                                                },
                                                {
                                                    displayName: Strings['en'].analytics.overview.assignment_stats.student_details.CREATED_DATE,
                                                    key:'created_at',
                                                    size: 3,
                                                    type: 'date'
                                                }
                                            ]}
                                        />
                                        :
                                        <div>
                                            <i className="font-style-italic">{Strings['en'].analytics.overview.assignment_stats.student_details.SELECT_PROMPT}</i>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                        :
                        <div className="margin-bottom-xl text-center">
                            <em className="font-style-italic">{Strings['en'].analytics.overview.assignment_stats.UPDATE_PROMPT}</em>
                        </div>
                    }
                    {(!!_publishedActCounts) &&
                        <div>
                            <h2 className="large-heading margin-bottom-lg" onDoubleClick={this.toggleForceShowNewStudents}>
                                {Strings['en'].analytics.overview.activity_stats.SECTION_HEADING}
                            </h2>
                            <div className="margin-bottom-lg">
                                <h3 className="md-heading margin-bottom-lg">
                                    {Strings['en'].analytics.overview.activity_stats.PUB_SECTION_HEADING}
                                </h3>
                                <div className="sub-heading">
                                    {Strings['en'].analytics.overview.activity_stats.PUB_SECTION_DESC}
                                </div>
                                    <SortableTable2
                                        className="width-100p"
                                        classNameTableContainer="tableContainerXScroll"
                                        showTotal={false}
                                        hideSearch
                                        uniqueRowKey="id"
                                        rows={_publishedActCounts}
                                        defaultSort={{ key: 'name', type: 'string', asc: true }}
                                        columns={[
                                            {
                                                displayName: Strings['en'].analytics.overview.activity_stats.TABLE_TEMPLATE_COL,
                                                key: 'name',
                                                size: 2,
                                                type: 'string'
                                            },
                                            {
                                                displayName: Strings['en'].analytics.overview.activity_stats.TABLE_PUB_ACT_COL,
                                                key: 'count',
                                                size: 2,
                                                type: 'int'
                                            },
                                        ]}
                                    />
                                    {(_publishedActCounts.length === 0) &&
                                        <div className="font-style-italic text-centered margin-top-md margin-bottom-lg">
                                            {Strings['en'].analytics.overview.activity_stats.NO_RECORDS_MSG}
                                        </div>
                                    }
                            </div>
                        </div>
                    }

                </div>
            </div>
        )
    }
}

ChannelAnalyticsView.propTypes = propTypes;
ChannelAnalyticsView.defaultProps = defaultProps;

export default withRouter(ChannelAnalyticsView);